import styled, { keyframes } from 'styled-components';
import { colors } from 'syngenta-digital-cropwise-react-ui-kit';
import { ensoLineColors, phaseColors } from './conditionsTheme';

export const ConditionsSection = styled.div`
  padding: 0;
  color: ${colors.neutral100};
  min-width: 0;
  h2,
  h3 {
    margin: 0;
    font-weight: 400;
    letter-spacing: 0;
  }
  h2 {
    font-size: 20px;
    line-height: 28px;
  }
  h3 {
    font-size: 20px;
    line-height: 28px;
  }
  p {
    margin: 8px 0 16px;
  }
  button,
  select {
    font: inherit;
  }
`;

export const ConditionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  label {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  select {
    max-width: 280px;
    min-width: 120px;
    padding: 8px;
    border: 1px solid ${colors.neutral30};
    border-radius: 4px;
    background: white;
  }
  @media (max-width: 600px) {
    label,
    select {
      width: 100%;
      max-width: 100%;
    }
  }
`;

export const ConditionsTabs = styled.div`
  display: flex;
  gap: 32px;
  border-bottom: 1px solid ${colors.neutral20};
  margin-bottom: 24px;
  button {
    padding: 12px 4px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    cursor: pointer;
    color: ${colors.neutral100};
    font-size: 14px;
    line-height: 20px;
  }
  button[aria-selected='true'] {
    border-bottom-color: ${colors.blue60};
    color: ${colors.blue60};
    font-weight: 600;
  }
  button:focus-visible {
    outline: 2px solid ${colors.blue60};
    outline-offset: 2px;
  }
`;

export const WeatherPanel = styled.div`
  padding: 32px;
  border: 1px solid ${colors.neutral30};
  border-radius: 12px;
  min-width: 0;
  margin-bottom: 24px;
  background: white;
  ${ConditionsTabs} {
    border: 0;
    margin-bottom: 0;
  }
  @media (max-width: 600px) {
    padding: 16px;
  }
`;

export const ChartToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0;
  small {
    color: ${colors.neutral70};
    font-size: 14px;
    line-height: 20px;
    text-align: right;
    padding: 10px;
  }
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    small {
      padding: 8px 4px;
      text-align: left;
      overflow-wrap: anywhere;
    }
  }
`;

export const EnvironmentalPanel = styled(WeatherPanel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 16px;
  h2 {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.3px;
  }
  ${ConditionsHeader} {
    margin-bottom: 0;
  }
  ${ChartToolbar} {
    margin: 0;
  }
`;

export const EnvironmentalHeading = styled.div`
  flex: 1;
  min-width: 0;
  p {
    color: ${colors.neutral90};
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.12px;
    margin: 8px 0 0;
  }
  @media (max-width: 600px) {
    flex-basis: 100%;
    h2 {
      overflow-wrap: anywhere;
    }
  }
`;

export const ChartScroll = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  .conditions-chart {
    width: 100%;
    min-width: 0;
  }
  .c3 text {
    font-family: inherit;
    font-size: 12px;
    fill: ${colors.neutral80};
  }
  .c3-axis path,
  .c3-axis line {
    stroke: ${colors.neutral30};
  }
  .c3-grid line {
    stroke: ${colors.neutral30};
    stroke-dasharray: 2 2;
  }
  .c3-region.phase-el_nino {
    fill: ${phaseColors.el_nino};
  }
  .c3-region.phase-la_nina {
    fill: ${phaseColors.la_nina};
  }
  .c3-region.phase-neutral {
    fill: ${phaseColors.neutral};
  }
  .c3-region.phase-unknown {
    fill: ${phaseColors.unknown};
  }
  .c3-region.planting-window {
    fill: ${phaseColors.el_nino};
  }
  .c3-circles-rainfall .c3-circle {
    fill: ${colors.green60} !important;
    stroke-width: 5px;
    stroke: ${colors.green20};
  }
  .c3-line {
    stroke-width: 1px;
  }
  .c3-target-enso-expected .c3-line {
    stroke-width: 2px;
  }
  .c3-target-enso-dotted .c3-line {
    stroke-dasharray: 2 4;
  }
  .conditions-tooltip,
  .c3-tooltip {
    padding: 8px;
    border-radius: 4px;
    background: ${colors.neutral90};
    box-shadow: 0 2px 4px #00000026;
    color: ${colors.neutral00};
    font-size: 14px;
    line-height: 20px;
    white-space: nowrap;
  }
  .conditions-tooltip-title,
  .c3-tooltip th {
    color: ${colors.neutral40};
    font-size: 12px;
    font-weight: 400;
    background: transparent;
  }
  .conditions-tooltip-value {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
  }
  .conditions-tooltip-marker {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
  }
  .conditions-tooltip-marker img:last-child {
    position: absolute;
    top: 30%;
    left: 30%;
  }
  .conditions-tooltip-phase-marker {
    display: inline-block;
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: inset 0 0 0 4px ${colors.neutral90}99;
  }
  .conditions-tooltip-phase-red {
    color: ${ensoLineColors.red};
  }
  .conditions-tooltip-phase-blue {
    color: ${ensoLineColors.blue};
  }
  .conditions-tooltip-phase-green {
    color: ${ensoLineColors.green};
  }
  .conditions-tooltip-phase-gray {
    color: ${ensoLineColors.gray};
  }
  .c3-tooltip td {
    border: 0;
    background: transparent;
  }
  @media (max-width: 600px) {
    .c3-axis-x text {
      font-size: 10px;
    }
    [data-mode='yearly'] .c3-axis-x text {
      font-size: 8px;
    }
    [data-mode='yearly'] .c3-circles-rainfall .c3-circle {
      stroke-width: 0;
    }
    .conditions-tooltip {
      font-size: 12px;
      line-height: 18px;
      white-space: normal;
      max-width: 240px;
      overflow-wrap: anywhere;
    }
    .conditions-tooltip-value {
      gap: 6px;
    }
  }
`;

export const MobileChartLabel = styled.div`
  display: none;
  @media (max-width: 600px) {
    display: block;
    color: ${colors.neutral70};
    font-size: 14px;
    line-height: 20px;
  }
`;

export const ChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  padding-top: 16px;
  margin: 16px auto 0;
  width: calc(100% - 100px);
  border-top: 1px solid ${colors.neutral20};
  color: ${colors.neutral90};
  font-size: 14px;
  line-height: 20px;
  text-transform: uppercase;
  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  img {
    display: block;
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    width: 100%;
    gap: 8px 16px;
    text-transform: none;
    border: 0;
    margin-top: 0;
    padding-top: 8px;
    font-size: 12px;
    line-height: 20px;
  }
`;

export const LegendDot = styled.i<{ $color: string }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

export const EnsoLegendLine = styled.i<{ $color: string; $dotted: boolean }>`
  display: inline-block;
  width: 14px;
  height: 0;
  border-top: 2px ${({ $dotted }) => ($dotted ? 'dotted' : 'solid')} ${({ $color }) => $color};
`;

export const FieldMap = styled.div`
  height: 360px;
  width: 100%;
  margin-bottom: 24px;
  overflow: hidden;
  border-radius: 8px;
  background: ${colors.neutral10};
`;

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const FieldsTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  border: 1px solid ${colors.neutral10};
  font-size: 14px;
  text-align: left;
  th,
  td {
    padding: 16px;
    vertical-align: top;
    border-bottom: 1px solid ${colors.neutral10};
  }
  thead {
    background: ${colors.neutral10};
  }
  thead th {
    height: 64px;
    font-size: 14px;
    font-weight: 600;
    vertical-align: middle;
    border-bottom-color: ${colors.neutral30};
  }
  tbody th {
    font-weight: 600;
    width: 23%;
    padding: 8px;
  }
  tbody tr {
    height: 146px;
  }
  tbody td:nth-child(2) {
    width: 15%;
  }
  tbody td:nth-child(3) {
    width: 42%;
    padding: 24px;
  }
`;

export const FieldConditionsPanel = styled(WeatherPanel)`
  border-radius: 16px;
  h2 {
    font-size: 24px;
    line-height: 32px;
  }
  > p {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 16px;
  }
  @media (max-width: 600px) {
    padding: 16px 0;
    border: 0;
    background: transparent;
  }
`;

export const FieldIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
  img {
    flex-shrink: 0;
  }
`;

export const FieldArea = styled.small`
  display: block;
  color: ${colors.neutral60};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const MobileFields = styled.div`
  width: 100%;
  min-width: 0;
  border: 1px solid ${colors.neutral20};
  border-radius: 4px;
  overflow: hidden;
`;

export const MobileField = styled.details`
  min-width: 0;
  border-bottom: 1px solid ${colors.neutral40};
  &:last-child {
    border-bottom: 0;
  }
  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px;
    min-height: 64px;
    background: ${colors.neutral10};
    cursor: pointer;
    list-style: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary:focus-visible {
    outline: 2px solid ${colors.blue60};
    outline-offset: -2px;
  }
  summary > img {
    flex-shrink: 0;
  }
  ${FieldIdentity} {
    flex: 1;
    padding-left: 12px;
    gap: 16px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    overflow-wrap: anywhere;
    > span {
      min-width: 0;
    }
  }
  .expanded-chevron {
    display: none;
  }
  &[open] {
    summary {
      border-bottom: 1px solid ${colors.neutral20};
    }
    .expanded-chevron {
      display: block;
    }
    .collapsed-chevron {
      display: none;
    }
  }
`;

export const MobileFieldMetrics = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 2px 8px;
  margin: 0;
  padding: 12px 12px 0 36px;
  font-size: 14px;
  line-height: 22px;
  dt {
    color: ${colors.neutral60};
  }
  dd {
    margin: 0;
    text-align: right;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

export const MobileFieldSoil = styled.div`
  padding: 0 24px 12px;
  min-width: 0;
  > div:first-child {
    padding-left: 12px;
    margin-bottom: 16px;
    color: ${colors.neutral60};
    font-size: 14px;
    line-height: 22px;
  }
`;

export const HeaderInfo = styled.img`
  vertical-align: middle;
`;

export const SoilLegend = styled.div`
  min-width: 280px;
  ul {
    padding: 0;
    margin: 8px 0 0;
    list-style: none;
  }
  @media (max-width: 600px) {
    min-width: 0;
    width: 100%;
  }
`;

export const SoilDistribution = styled.div`
  display: flex;
  gap: 2px;
  height: 14px;
  overflow: hidden;
  border-radius: 3px;
`;

export const SoilSegment = styled.span<{ $color: string; $percentage: number }>`
  flex: ${({ $percentage }) => $percentage} 1 0;
  background: ${({ $color }) => $color};
`;

export const SoilLegendRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 25px;
  font-size: 12px;
  line-height: 18px;
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 11px minmax(0, 1fr) auto;
    gap: 0 8px;
    margin-top: 16px;
    font-size: 14px;
    line-height: 20px;
    > span:first-child {
      grid-row: 1 / 3;
    }
  }
`;

export const SoilSwatch = styled.span<{ $color: string }>`
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

export const SoilDescription = styled.span`
  flex: 1;
  strong {
    font-weight: 600;
  }
  small {
    font-size: 10px;
    color: ${colors.neutral60};
    white-space: nowrap;
  }
  @media (max-width: 600px) {
    grid-column: 2 / -1;
    min-width: 0;
    overflow-wrap: anywhere;
    strong {
      font-weight: 400;
    }
    small {
      font-size: inherit;
      color: inherit;
      white-space: normal;
    }
  }
`;

export const SoilQuantity = styled.span`
  white-space: nowrap;
  @media (max-width: 600px) {
    grid-column: 2;
    white-space: normal;
    min-width: 0;
  }
`;

export const SoilClay = styled.span`
  color: ${colors.neutral60};
  font-size: 11px;
  text-align: right;
  min-width: 24px;
  @media (max-width: 600px) {
    grid-column: 3;
    font-size: 14px;
  }
`;

export const SoilLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  ${LegendDot} {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }
`;

const skeletonPulse = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
`;

export const SoilSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 160px;
  padding: 4px 0;
  > span {
    display: flex;
    align-items: center;
    gap: 8px;
    &::before,
    &::after {
      content: '';
      display: block;
      background: ${colors.neutral20};
      animation: ${skeletonPulse} 1.4s ease-in-out infinite;
    }
    &::before {
      width: 10px;
      height: 10px;
      border-radius: 2px;
      flex-shrink: 0;
    }
    &::after {
      height: 12px;
      border-radius: 4px;
      flex: 1;
    }
    &:last-child::after {
      flex: 0 1 60%;
    }
  }
`;

export const ChartLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 312px;
  border: 1px dashed ${colors.neutral20};
  border-radius: 8px;
  background: ${colors.neutral10};
  color: ${colors.neutral60};
  @media (max-width: 600px) {
    min-height: 220px;
  }
`;

export const FieldMetrics = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  margin: 0 0 24px;
  dt {
    color: ${colors.neutral70};
    font-size: 14px;
  }
  dd {
    margin: 8px 0 0;
    font-size: 20px;
    overflow-wrap: anywhere;
  }
  small {
    display: block;
    font-size: 12px;
    color: ${colors.neutral70};
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;
